import { skinSeoulProductsClient } from '@/infrastructure/SkinSeoulProductsClient';

/** One catalog hit a search offers: the sku being looked for, and the words that identify it. */
export interface CatalogHit {
  sku: string;
  brand: string;
  productName: string;
}

/** Long enough that the catalog is not searched on the first keystroke of a sku someone is typing. */
export const CATALOG_SEARCH_MIN_LENGTH = 2;
const SEARCH_LIMIT = 10;

/** A sku is all digits; anything else is a name or a brand and only the catalog search can answer it. */
const looksLikeSku = (term: string): boolean => /^\d{4,}$/.test(term);

/** How a hit reads in a suggestion list: brand first, like every other fulfillment screen. */
export const catalogHitLabel = (hit: CatalogHit): string => [hit.brand, hit.productName].filter(Boolean).join(' — ');

/**
 * One term over sku, brand and product name, because the operator types whichever identifier they
 * happen to know. A failed lookup rejects, so each caller decides what a silent catalog means on its screen.
 */
export const findCatalogHits = async (term: string): Promise<CatalogHit[]> => {
  const typed = term.trim();
  // The catalog call matches names only, so an operator holding a sku number would never find their product.
  const [byName, bySku] = await Promise.all([
    skinSeoulProductsClient.searchProductsByName(typed, SEARCH_LIMIT),
    looksLikeSku(typed) ? skinSeoulProductsClient.searchProductsBySku(typed) : Promise.resolve([]),
  ]);
  const found = [...bySku, ...(byName.products ?? [])].map((hit) => ({
    sku: hit.sku,
    brand: hit.brand ?? '',
    productName: hit.name ?? '',
  }));
  // The sku match leads, so the cap never drops the one product the operator named exactly.
  return found
    .filter((hit, index) => found.findIndex((other) => other.sku === hit.sku) === index)
    .slice(0, SEARCH_LIMIT);
};
